import {
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";
import { Column } from "@pufflig/prompt-studio-types";
import { EmptyState, Icon } from "@pufflig/prompt-studio-ui";
import { toggleColumnVisibility } from "../state/actions/toggleColumnVisibility";
import { useState } from "react";
import { EditColumnModal } from "./EditColumnModal";

interface TableListPanelProps {
  columns?: Column[];
  onUpdateColumn?: (column: Column) => void;
}

export const TableListPanel = ({ columns = [], onUpdateColumn }: TableListPanelProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedColumn, setSelectedColumn] = useState<Column | null>(null);
  const hasColumns = columns.length > 0;

  const handleEdit = (column: Column) => {
    setSelectedColumn(column);
    onOpen();
  };
  return (
    <Flex flex="1" overflow="auto" padding="spacing-md">
      {!hasColumns ? (
        <EmptyState title="No Columns" showBackground={false} />
      ) : (
        <TableContainer flex="1" overflowX="auto">
          <Table>
            <Thead height="3.375rem">
              <Tr>
                <Th
                  padding="0"
                  px="1.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  bg="background.primary"
                >
                  <Text textStyle="label/S/regular" color="foreground.secondary">
                    Column name
                  </Text>
                </Th>
                <Th
                  padding="0"
                  px="1.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  bg="background.primary"
                >
                  <Text textStyle="label/S/regular" color="foreground.secondary">
                    Description
                  </Text>
                </Th>
                <Th
                  padding="0"
                  px="1.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  bg="background.primary"
                >
                  <Text textStyle="label/S/regular" color="foreground.secondary">
                    Data type
                  </Text>
                </Th>
                <Th
                  padding="0"
                  px="1.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  bg="background.primary"
                >
                  <Text textStyle="label/S/regular" color="foreground.secondary">
                    Example values
                  </Text>
                </Th>
                <Th
                  padding="0"
                  px="1.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  bg="background.primary"
                >
                  <Text textStyle="label/S/regular" color="foreground.secondary">
                    Synonyms
                  </Text>
                </Th>
                <Th
                  padding="0"
                  px="0.5rem"
                  borderBottom="1px solid"
                  borderColor="border.secondary"
                  textTransform="none"
                  width="6rem"
                  bg="background.primary"
                />
              </Tr>
            </Thead>
            <Tbody>
              {columns.map((column) => (
                <Tr
                  key={column.columnName}
                  _hover={{ bg: "background.secondary" }}
                  height="3.375rem"
                  opacity={column.hidden ? 0.5 : 1}
                >
                  <Td
                    bg="background.primary"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    overflow="hidden"
                  >
                    {column.columnName}
                  </Td>
                  <Td
                    bg="background.primary"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    overflow="hidden"
                    overflowWrap="break-word"
                    textOverflow={"ellipsis"}
                  >
                    <Tooltip
                      label={column.columnDescription || ""}
                      placement="top"
                      hasArrow
                      isDisabled={!column.columnDescription}
                    >
                      <Text
                        textStyle="body/S/regular"
                        color="foreground.primary"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {column.columnDescription || "-"}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td
                    bg="background.primary"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    overflow="hidden"
                  >
                    {column.dataType}
                  </Td>
                  <Td
                    bg="background.primary"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    overflow="hidden"
                  >
                    {column.examples?.join(", ")}
                  </Td>
                  <Td
                    bg="background.primary"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="1.5rem"
                    overflow="hidden"
                  >
                    {column.synonyms?.join(", ")}
                  </Td>
                  <Td
                    bg="background.primary"
                    width="4rem"
                    border="none"
                    borderBottom="1px solid"
                    borderColor="border.secondary"
                    padding="0"
                    px="0.5rem"
                    overflow="hidden"
                  >
                    <IconButton
                      aria-label={column.hidden ? "Show column" : "Hide column"}
                      icon={<Icon size="sm" name={column.hidden ? "eye" : "eye-slash"} />}
                      size="sm"
                      variant="ghost"
                      _hover={{ bg: "background.tertiary" }}
                      onClick={() => toggleColumnVisibility(column.columnName)}
                      mr="spacing-xxs"
                    />
                    <IconButton
                      aria-label="Edit column"
                      icon={<Icon size="sm" name="edit" />}
                      size="sm"
                      variant="ghost"
                      _hover={{ bg: "background.tertiary" }}
                      onClick={() => handleEdit(column)}
                      isDisabled={column.hidden}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <EditColumnModal
        isOpen={isOpen}
        onClose={() => {
          setSelectedColumn(null);
          onClose();
        }}
        column={selectedColumn}
        onSave={(c) => onUpdateColumn?.(c)}
      />
    </Flex>
  );
};
